'use client';

import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Formik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import AlertBox from 'shared/components/presentational/AlertBox/AlertBox';
import Loader from 'shared/blocks/selfcare/shared/components/Loader';
import {
  AlertBoxContainer,
  Form as RenewalForm,
  HeadContainer,
  Subtitle,
  Wrapper,
  RenewalNumber,
  ButtonsWrapper,
  Card,
  DetainedOffer,
  OfferTitle,
  EndingDate,
  CardContent,
  CardContentWrapperLeft,
  OptionsContainer,
  OptionsTitle
} from '../styles/components';
import { Button } from '../../../shared/components/Button/Button';
import { useOfferSearch } from '../hooks/useOfferSearch';
import { FormattedProps } from '../types';
import ResponsiveHoc, {
  ResponsiveHocProps
} from 'shared/components/presentational/ResponsiveHOC/ResponsiveHoc';
import { CustomTitle } from 'shared/components/presentational/Title/sharedTitles';
import OfferDetailsRoc from '../../../shared/containers/OfferDetailsRocContainer';
import MinorOptionsList from './MinorOptionsList';
import { ADD_OPERATORS, SAVE_FORM, UpdatingOptionsState } from '../../reducer';

function Form({
  title,
  majorProducts,
  minorProductsTitle,
  minorProductsEngagementUnit,
  alertBox,
  submitButton,
  previousButton,
  redirectLinkToStart,
  apis: { crmManagementActsOfferSearch },
  saleDeviceId,
  mediaQueries
}: FormattedProps & ResponsiveHocProps) {
  const { t } = useTranslation();
  const history = useHistory();

  const dispatch = useDispatch();
  const updatingOptionsValues = useSelector(
    (state: { updatingOptions: UpdatingOptionsState }) =>
      state.updatingOptions || {}
  );

  const { data, isError, isLoading: isLoadingOfferSearchData } = useOfferSearch(
    {
      crmManagementActsOfferSearch,
      saleDeviceId,
      equipmentId: updatingOptionsValues.renewalNumber,
      majorProducts: majorProducts.products
    }
  );

  if (isLoadingOfferSearchData) {
    return <Loader />;
  }

  if (mediaQueries.toPhone === false) {
    return (
      <AlertBoxContainer>
        <AlertBox
          status="info"
          content={t('blocks.updatingOptions.onlyMobile')}
        />
      </AlertBoxContainer>
    );
  }

  if (isError || !data) {
    return (
      <AlertBoxContainer>
        <AlertBox
          status="error"
          content={<Trans i18nKey="blocks.updatingOptions.error.description" />}
          link={{
            label: t('blocks.updatingOptions.error.linkLabel'),
            href: redirectLinkToStart
          }}
        />
      </AlertBoxContainer>
    );
  }

  if (!data.majorOffer) {
    return (
      <AlertBoxContainer>
        <AlertBox
          status="error"
          content={t('blocks.updatingOptions.form.error.noOffer')}
        />
      </AlertBoxContainer>
    );
  }

  const getDisabledMinorOptions = (
    formSelectedOptions: Record<string, boolean>
  ) => {
    // Get the IDs of the selected minor options
    const selectedOptionsIds = Object.keys(formSelectedOptions).filter(
      optionId => formSelectedOptions[optionId]
    );

    let disabledMinorOptionsIds: string[] = [];

    // For each selected minor option, collect the forbidden options
    selectedOptionsIds.forEach(option => {
      disabledMinorOptionsIds = disabledMinorOptionsIds.concat(
        data.minorsOffers.toDisplay[option].forbiddenOptions
      );
    });

    // Disable minor options that are detained to keep
    Object.values(data.minorsOffers.detainedToKeep).forEach(option => {
      disabledMinorOptionsIds = disabledMinorOptionsIds.concat(
        option.forbiddenOptions
      );
    });

    // Return a list of IDs for minor options that should be disabled
    return disabledMinorOptionsIds;
  };

  return (
    <Wrapper>
      <HeadContainer>
        {title && (
          <CustomTitle htmlTag={title.htmlTag}>{title.label}</CustomTitle>
        )}
        <div>
          <Subtitle>
            {t('blocks.updatingOptions.form.renewalNumber')}&nbsp;
            <RenewalNumber>{updatingOptionsValues.renewalNumber}</RenewalNumber>
          </Subtitle>
        </div>
      </HeadContainer>
      <Formik
        initialValues={{
          minorsOffers:
            updatingOptionsValues.formValues.minorsOffers &&
            Object.keys(updatingOptionsValues.formValues.minorsOffers)
              .length !== 0
              ? updatingOptionsValues.formValues.minorsOffers
              : Object.entries(data.minorsOffers.toDisplay).reduce(
                  (acc, [id, option]) => {
                    if (option.detained === true) {
                      acc[id] = true;
                    }
                    return acc;
                  },
                  {} as Record<string, boolean>
                )
        }}
        onSubmit={values => {
          dispatch({
            type: SAVE_FORM,
            payload: {
              formValues: { ...values, majorOfferId: data.majorOffer!.id },
              saleDeviceId
            }
          });
          dispatch({
            type: ADD_OPERATORS,
            payload: {
              operators: data.operators
            }
          });
          if (submitButton.link) {
            const minorsOffersIdsSelected = Object.entries(values.minorsOffers)
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              .filter(([_, isSelected]) => !!isSelected)
              .map(([id]) => id);
            const params = new URLSearchParams({
              product: data.majorOffer!.id
            });

            // Minors offers
            const minorsOffersIds = [];
            if (minorsOffersIdsSelected.length > 0) {
              minorsOffersIds.push(...minorsOffersIdsSelected);
            }
            const minorsOffersIdsDetained = Object.keys(
              data.minorsOffers.detainedToKeep
            );
            if (minorsOffersIdsDetained.length > 0) {
              minorsOffersIds.push(...minorsOffersIdsDetained);
            }
            if (minorsOffersIds.length !== 0) {
              params.append('minorProducts', minorsOffersIds.join(','));
            }

            history.push(`${submitButton.link}?${params.toString()}`);
          }
        }}
      >
        {({ values, setFieldValue }) => {
          const disabledMinorOptionsIds = getDisabledMinorOptions(
            values.minorsOffers
          );

          return (
            <RenewalForm>
              <Card>
                <DetainedOffer>
                  {t('blocks.updatingOptions.form.detainedOffer')}
                </DetainedOffer>
                <CardContent>
                  <CardContentWrapperLeft>
                    <OfferTitle htmlTag={'h2'}>
                      {data.majorOffer!.title}
                    </OfferTitle>
                    <EndingDate>
                      {majorProducts.endingDateLabel}&nbsp;
                      {data.contractEndDate}
                    </EndingDate>
                  </CardContentWrapperLeft>
                  <OfferDetailsRoc
                    price={data.majorOffer!.price}
                    engagementUnit={data.majorOffer!.engagementUnit}
                    currency={data.majorOffer!.currency}
                    variant="modal"
                  />
                </CardContent>
              </Card>

              <OptionsContainer>
                <OptionsTitle htmlTag={minorProductsTitle.htmlTag}>
                  {minorProductsTitle.label}
                </OptionsTitle>

                <MinorOptionsList
                  majorOffer={data.majorOffer!}
                  minorOffers={data.minorsOffers.toDisplay}
                  disabledMinorOptionsIds={disabledMinorOptionsIds}
                  minorProductsEngagementUnit={minorProductsEngagementUnit}
                  setFieldValue={setFieldValue}
                  values={values}
                />

                {alertBox && <AlertBox {...alertBox} />}
              </OptionsContainer>

              <ButtonsWrapper>
                <Button
                  label={submitButton.label}
                  type="submit"
                  variant="primary"
                  fullWidthMobile
                />

                {previousButton && previousButton.link && (
                  <Button
                    label={previousButton.label}
                    variant="secondary"
                    fullWidthMobile
                    link={previousButton.link}
                  />
                )}
              </ButtonsWrapper>
            </RenewalForm>
          );
        }}
      </Formik>
    </Wrapper>
  );
}

export default ResponsiveHoc(Form);
